/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import styles from "./GlobalTabBar.module.css";

import useIsMobile from "../../../helpers/useIsMobile";
import TabAssessmentIcon from "./icons/TabAssessmentIcon";
import TabConnectionsIcon from "./icons/TabConnectionsIcon";
import TabResultsIcon from "./icons/TabResultsIcon";
import TabSettingsIcon from "./icons/TabSettingsIcon";
import TabHomeIcon from "./icons/TabHomeIcon";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import VerySmallProfileImage from "../SmallProfileImage/VerySmallProfileImage";
import TabIconWrapper from "./icons/TabIconWrapper";
import { useAuth } from "@/context/AuthContext";
import GoDeeperIcon from "@/components/GoDeeper/GoDeeperIcon";
import { useModalContext } from "@/context/ModalContext";
import GoDeeperModal from "@/components/GoDeeper/GoDeeperModal/GoDeeperModal";

type BaseRouteEnum =
  | "home"
  | "assessment"
  | "results"
  | "connections"
  | "profile"
  | "settings"
  | "";

export default function GlobalTabBar() {
  const [currentRoute, setCurrentRoute] = useState<BaseRouteEnum>("");
  const route = useRouter().route;
  const { slug } = useRouter().query;
  const isMobile = useIsMobile();
  const userState = useSelector((state: RootState) => state.user);
  const { user } = useAuth();

  const { setModalComponent } = useModalContext();

  useEffect(() => {
    setCurrentRoute(route.split("/")[2] as BaseRouteEnum);
    if ((route.split("/")[2] as BaseRouteEnum) === "profile") {
      if (slug !== user?.uid) {
        setCurrentRoute("connections");
      }
    } else if (route.split("/")[2] === "messaging") {
      setCurrentRoute("home");
    }
    if (!isMobile) {
      document.body.style.overflowX = "visible";
    }

    return () => {
      document.body.style.overflowX = "hidden";
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  return (
    <div className={styles.main}>
      <div className={styles.logo}>
        <Link style={{ cursor: "auto" }} href={`/app/profile`}>
          <img
            className="logo"
            src="https://global-uploads.webflow.com/5ddd2caed93ad8355b40f958/62656cd5e8ee77d13eec117d_dimensional-logo.png"
            loading="lazy"
            width="100"
            alt="Dimensional"
          />
        </Link>

        <svg
          className="mobile-logo"
          width="138"
          height="20"
          viewBox="0 0 138 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.815273 19V1.10294H6.61903C8.71555 1.10294 10.4371 1.4694 11.7836 2.20233C13.1472 2.93526 14.1528 3.97499 14.8005 5.32153C15.4653 6.65103 15.7977 8.2362 15.7977 10.077C15.7977 11.9008 15.4653 13.486 14.8005 14.8325C14.1528 16.162 13.1472 17.1932 11.7836 17.9262C10.4371 18.6421 8.71555 19 6.61903 19H0.815273ZM3.5254 16.6989H6.51677C8.13602 16.6989 9.41439 16.4348 10.3519 15.9064C11.3064 15.378 11.9882 14.6195 12.3972 13.6309C12.8233 12.6423 13.0364 11.4577 13.0364 10.077C13.0364 8.69641 12.8233 7.50327 12.3972 6.49762C11.9882 5.49198 11.3064 4.72496 10.3519 4.19657C9.41439 3.65114 8.13602 3.37842 6.51677 3.37842H3.5254V16.6989ZM19.457 3.91533C18.9456 3.91533 18.5195 3.76193 18.1786 3.45512C17.8548 3.13127 17.6928 2.73072 17.6928 2.25346C17.6928 1.77621 17.8548 1.38418 18.1786 1.07737C18.5195 0.753521 18.9456 0.591595 19.457 0.591595C19.9683 0.591595 20.3859 0.753521 20.7098 1.07737C21.0507 1.38418 21.2211 1.77621 21.2211 2.25346C21.2211 2.73072 21.0507 3.13127 20.7098 3.45512C20.3859 3.76193 19.9683 3.91533 19.457 3.91533ZM18.1019 19V6.31865H20.812V19H18.1019ZM23.6226 19V6.31865H26.026L26.2561 8.10836C26.6651 7.46066 27.2021 6.94931 27.8668 6.57433C28.5486 6.19934 29.3327 6.01185 30.219 6.01185C32.2303 6.01185 33.6279 6.80443 34.412 8.3896C34.8722 7.65667 35.4858 7.07715 36.2529 6.65103C37.0369 6.22491 37.8806 6.01185 38.784 6.01185C40.3692 6.01185 41.6134 6.4891 42.5168 7.44361C43.4202 8.39812 43.8719 9.82136 43.8719 11.7133V19H41.1618V11.9946C41.1618 9.54013 40.2243 8.3129 38.3494 8.3129C37.3949 8.3129 36.6108 8.67084 35.9972 9.38672C35.4006 10.1026 35.1023 11.1253 35.1023 12.4548V19H32.3922V11.9946C32.3922 9.54013 31.4462 8.3129 29.5542 8.3129C28.6168 8.3129 27.8412 8.67084 27.2276 9.38672C26.6311 10.1026 26.3328 11.1253 26.3328 12.4548V19H23.6226ZM52.2072 19.3068C50.9629 19.3068 49.855 19.0341 48.8835 18.4887C47.929 17.9262 47.179 17.1506 46.6335 16.162C46.0881 15.1734 45.8154 14.0229 45.8154 12.7105C45.8154 11.381 46.0796 10.2134 46.608 9.20775C47.1534 8.20211 47.9034 7.41804 48.8579 6.85557C49.8294 6.29309 50.9544 6.01185 52.2328 6.01185C53.477 6.01185 54.5594 6.29309 55.4798 6.85557C56.4002 7.401 57.1161 8.13393 57.6275 9.05435C58.1388 9.97477 58.3945 10.9889 58.3945 12.0968C58.3945 12.2673 58.3859 12.4548 58.3689 12.6593C58.3689 12.8468 58.3604 13.0599 58.3433 13.2985H48.4744C48.5596 14.5257 48.9602 15.4632 49.676 16.1109C50.409 16.7416 51.2527 17.0569 52.2072 17.0569C52.9742 17.0569 53.6134 16.8864 54.1247 16.5455C54.6531 16.1876 55.0452 15.7103 55.3008 15.1138H58.011C57.6701 16.3069 56.9883 17.304 55.9656 18.1051C54.9599 18.9063 53.7071 19.3068 52.2072 19.3068ZM52.2072 8.2362C51.3038 8.2362 50.5027 8.50891 49.8039 9.05435C49.105 9.58274 48.6789 10.3838 48.5255 11.4577H55.6843C55.6332 10.4691 55.2838 9.68501 54.6361 9.10548C53.9884 8.52596 53.1788 8.2362 52.2072 8.2362ZM60.4394 19V6.31865H62.8428L63.0473 8.543C63.4393 7.75894 64.0103 7.14533 64.7603 6.70216C65.5273 6.24195 66.4051 6.01185 67.3937 6.01185C68.9278 6.01185 70.1294 6.4891 70.9987 7.44361C71.885 8.39812 72.3282 9.82136 72.3282 11.7133V19H69.6437V11.9946C69.6437 9.54013 68.638 8.3129 66.6267 8.3129C65.6211 8.3129 64.7859 8.67084 64.1211 9.38672C63.4734 10.1026 63.1496 11.1253 63.1496 12.4548V19H60.4394ZM79.8154 19.3068C78.2132 19.3068 76.8922 18.9148 75.8525 18.1307C74.8128 17.3467 74.2162 16.3069 74.0628 15.0115H76.7985C76.9348 15.591 77.2587 16.0939 77.77 16.52C78.2814 16.9291 78.9546 17.1336 79.7898 17.1336C80.608 17.1336 81.2046 16.9631 81.5795 16.6222C81.9545 16.2814 82.142 15.8893 82.142 15.4462C82.142 14.7985 81.8778 14.3638 81.3494 14.1422C80.8381 13.9036 80.1222 13.6905 79.2018 13.503C78.4859 13.3496 77.77 13.1451 77.0541 12.8894C76.3553 12.6338 75.7673 12.2758 75.29 11.8156C74.8298 11.3384 74.5997 10.6992 74.5997 9.89807C74.5997 8.79015 75.0258 7.86973 75.8781 7.13681C76.7303 6.38683 77.9234 6.01185 79.4575 6.01185C80.8722 6.01185 82.0142 6.35274 82.8835 7.03454C83.7698 7.71633 84.2897 8.67936 84.4431 9.92363H81.8352C81.75 9.3782 81.4943 8.95208 81.0682 8.64527C80.6591 8.33847 80.1052 8.18506 79.4063 8.18506C78.7245 8.18506 78.1962 8.32994 77.8212 8.6197C77.4462 8.89242 77.2587 9.25036 77.2587 9.69353C77.2587 10.1367 77.5144 10.4861 78.0257 10.7418C78.5541 10.9975 79.2444 11.2276 80.0966 11.4321C80.9489 11.6196 81.733 11.8412 82.4488 12.0968C83.1818 12.3355 83.7698 12.6934 84.213 13.1707C84.6561 13.6479 84.8777 14.3468 84.8777 15.2672C84.8948 16.4262 84.4431 17.3893 83.5227 18.1563C82.6193 18.9233 81.3835 19.3068 79.8154 19.3068ZM88.6033 3.91533C88.092 3.91533 87.6659 3.76193 87.325 3.45512C87.0011 3.13127 86.8392 2.73072 86.8392 2.25346C86.8392 1.77621 87.0011 1.38418 87.325 1.07737C87.6659 0.753521 88.092 0.591595 88.6033 0.591595C89.1147 0.591595 89.5323 0.753521 89.8561 1.07737C90.197 1.38418 90.3675 1.77621 90.3675 2.25346C90.3675 2.73072 90.197 3.13127 89.8561 3.45512C89.5323 3.76193 89.1147 3.91533 88.6033 3.91533ZM87.2483 19V6.31865H89.9584V19H87.2483ZM98.7006 19.3068C97.4904 19.3068 96.3996 19.0341 95.428 18.4887C94.4735 17.9262 93.715 17.1506 93.1525 16.162C92.59 15.1564 92.3088 13.9888 92.3088 12.6593C92.3088 11.3298 92.59 10.1708 93.1525 9.18218C93.732 8.17654 94.5076 7.401 95.4791 6.85557C96.4507 6.29309 97.533 6.01185 98.7262 6.01185C99.9364 6.01185 101.019 6.29309 101.973 6.85557C102.945 7.401 103.712 8.17654 104.274 9.18218C104.854 10.1708 105.144 11.3298 105.144 12.6593C105.144 13.9888 104.854 15.1564 104.274 16.162C103.712 17.1506 102.945 17.9262 101.973 18.4887C101.002 19.0341 99.9108 19.3068 98.7006 19.3068ZM98.7006 16.9802C99.3483 16.9802 99.9449 16.8183 100.49 16.4944C101.053 16.1706 101.504 15.6933 101.845 15.0626C102.186 14.4149 102.357 13.6138 102.357 12.6593C102.357 11.7048 102.186 10.9122 101.845 10.2816C101.522 9.63387 101.078 9.14809 100.516 8.82424C99.9704 8.50039 99.3739 8.33847 98.7262 8.33847C98.0785 8.33847 97.4734 8.50039 96.9109 8.82424C96.3655 9.14809 95.9223 9.63387 95.5814 10.2816C95.2405 10.9122 95.0701 11.7048 95.0701 12.6593C95.0701 13.6138 95.2405 14.4149 95.5814 15.0626C95.9223 15.6933 96.3655 16.1706 96.9109 16.4944C97.4563 16.8183 98.0529 16.9802 98.7006 16.9802ZM107.307 19V6.31865H109.711L109.915 8.543C110.307 7.75894 110.878 7.14533 111.628 6.70216C112.395 6.24195 113.273 6.01185 114.262 6.01185C115.796 6.01185 116.997 6.4891 117.867 7.44361C118.753 8.39812 119.196 9.82136 119.196 11.7133V19H116.512V11.9946C116.512 9.54013 115.506 8.3129 113.495 8.3129C112.489 8.3129 111.654 8.67084 110.989 9.38672C110.341 10.1026 110.017 11.1253 110.017 12.4548V19H107.307ZM125.916 19.3068C124.842 19.3068 123.956 19.1278 123.257 18.7699C122.558 18.412 122.039 17.9432 121.698 17.3637C121.357 16.7671 121.186 16.1194 121.186 15.4206C121.186 14.1934 121.664 13.2218 122.618 12.5059C123.573 11.79 124.936 11.4321 126.709 11.4321H130.033V11.202C130.033 10.2134 129.76 9.47195 129.214 8.97765C128.686 8.48335 127.996 8.2362 127.144 8.2362C126.394 8.2362 125.737 8.42369 125.175 8.79868C124.629 9.15662 124.297 9.69353 124.178 10.4094H121.468C121.553 9.48899 121.86 8.70493 122.388 8.05723C122.933 7.39248 123.615 6.88966 124.433 6.54876C125.269 6.19082 126.181 6.01185 127.169 6.01185C128.942 6.01185 130.314 6.48058 131.285 7.41805C132.257 8.33847 132.743 9.59978 132.743 11.202V19H130.391L130.16 16.8268C129.803 17.5256 129.283 18.1137 128.601 18.5909C127.919 19.0682 127.024 19.3068 125.916 19.3068ZM126.453 17.108C127.186 17.108 127.8 16.9376 128.294 16.5967C128.805 16.2387 129.197 15.77 129.47 15.1905C129.76 14.611 129.939 13.9718 130.007 13.2729H126.99C125.916 13.2729 125.149 13.4604 124.689 13.8354C124.246 14.2104 124.024 14.6791 124.024 15.2416C124.024 15.8211 124.237 16.2814 124.664 16.6222C125.107 16.9461 125.703 17.108 126.453 17.108ZM135.136 19V0.591595H137.846V19H135.136Z"
            fill="#FFFFF2"
          />
        </svg>
      </div>
      <div className={styles.wrapper}>
        <Link
          style={{ cursor: "auto" }}
          href={"/app/home"}
          className={styles.tabChild}
        >
          <TabIconWrapper>
            <TabHomeIcon active={currentRoute === "home"} />
          </TabIconWrapper>
          <p className={currentRoute === "home" ? styles.active : ""}>Home</p>
        </Link>
        <Link
          onClick={() => {
            localStorage.setItem("seenChatbotTooltip", "true");
          }}
          style={{ cursor: "auto" }}
          href={"/app/assessment"}
          className={styles.tabChild}
        >
          <TabIconWrapper>
            <TabAssessmentIcon active={currentRoute === "assessment"} />
          </TabIconWrapper>
          <p className={currentRoute === "assessment" ? styles.active : ""}>
            Assessments
          </p>
        </Link>

        <div
          onClick={() => {
            setModalComponent(<GoDeeperModal />);
            localStorage.setItem("seenChatbotTooltip", "true");
          }}
          style={{ cursor: "auto" }}
          className={styles.tabChild}
        >
          <TabIconWrapper>
            <GoDeeperIcon />
          </TabIconWrapper>
          <p>Go deep</p>
        </div>

        <Link
          onClick={() => {
            localStorage.setItem("seenChatbotTooltip", "true");
          }}
          style={{ cursor: "auto" }}
          href={"/app/results"}
          className={styles.tabChild}
        >
          <TabIconWrapper>
            <TabResultsIcon active={currentRoute === "results"} />
          </TabIconWrapper>
          <p className={currentRoute === "results" ? styles.active : ""}>
            Results
          </p>
        </Link>

        <Link
          onClick={() => {
            localStorage.setItem("seenChatbotTooltip", "true");
          }}
          href={`/app/profile/${userState.publicProfileData?.ownerUUID}`}
          className={`${styles.tabChild} ${styles.hideDesktop}`}
          style={{ cursor: "auto" }}
        >
          <VerySmallProfileImage
            imageUrl={userState.imageURL ?? "/default-image.png"}
            publicProfile={userState.publicProfileData}
            noLink
          />
          <p className={currentRoute === "profile" ? styles.active : ""}>
            Profile
          </p>
        </Link>
        <Link
          onClick={() => {
            localStorage.setItem("seenChatbotTooltip", "true");
          }}
          href={`/app/profile/${userState.publicProfileData?.ownerUUID}`}
          className={`${styles.tabChild} ${styles.hideMobile}`}
          style={{ cursor: "auto" }}
        >
          <VerySmallProfileImage
            noLink
            imageUrl={userState.imageURL ?? "/default-image.png"}
            publicProfile={userState.publicProfileData}
          />
          <p className={currentRoute === "profile" ? styles.active : ""}>
            Profile
          </p>
        </Link>

        <Link
          href={"/app/settings"}
          className={`${styles.tabChild} ${styles.hideMobile}`}
          style={{ cursor: "auto" }}
        >
          <div className={styles.tabChild}>
            <TabIconWrapper>
              <TabSettingsIcon active={currentRoute === "settings"} />
            </TabIconWrapper>
            <p className={currentRoute === "settings" ? styles.active : ""}>
              Settings
            </p>
          </div>
        </Link>
      </div>
    </div>
  );
}
